// Fuso horário do tenant. Toda data exibida ou digitada segue este fuso, nunca o do aparelho.
// A API grava instantes em UTC; aqui eles viram horário de parede do tenant e vice-versa.
export const DEFAULT_TIME_ZONE = 'America/Sao_Paulo'

const LOCALE = 'pt-BR'
const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/

/** Fusos oficiais do Brasil, do padrão (Brasília) aos demais. */
export const BRAZIL_TIME_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Noronha',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Rio_Branco',
] as const

let activeTimeZone = DEFAULT_TIME_ZONE

export function isSupportedTimeZone(zone: string) {
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: zone })
    return true
  } catch {
    return false
  }
}

export function getTimeZone() {
  return activeTimeZone
}

/** Define o fuso do tenant; valores ausentes ou desconhecidos pelo navegador voltam ao padrão. */
export function setTimeZone(zone?: string | null) {
  activeTimeZone = zone && isSupportedTimeZone(zone) ? zone : DEFAULT_TIME_ZONE
}

type DateInput = string | number | Date

export function formatZonedDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions) {
  return new Date(value).toLocaleString(LOCALE, { ...options, timeZone: getTimeZone() })
}

export function formatZonedDate(value: DateInput, options?: Intl.DateTimeFormatOptions) {
  return new Date(value).toLocaleDateString(LOCALE, { ...options, timeZone: getTimeZone() })
}

export function formatZonedTime(value: DateInput, options?: Intl.DateTimeFormatOptions) {
  return new Date(value).toLocaleTimeString(LOCALE, { ...options, timeZone: getTimeZone() })
}

/** Instante → "YYYY-MM-DDTHH:mm" no fuso, o formato do <input type="datetime-local">. */
export function zonedInput(value: DateInput, zone = getTimeZone()) {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value))
  return parts.replace(' ', 'T')
}

/** Instante → dia "YYYY-MM-DD" no fuso. */
export function zonedDateKey(value: DateInput, zone = getTimeZone()) {
  return zonedInput(value, zone).slice(0, 10)
}

export function todayKey(now: DateInput = new Date()) {
  return zonedDateKey(now)
}

/** Soma dias a um dia "YYYY-MM-DD" sem passar por fuso algum. */
export function addDaysToKey(key: string, days: number) {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** Dia da semana (0 = domingo) de um dia "YYYY-MM-DD". */
export function weekdayOfKey(key: string) {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

/** Formata um dia "YYYY-MM-DD" (sem horário) exatamente como foi escrito. */
export function formatDateKey(key: string, options?: Intl.DateTimeFormatOptions) {
  const noon = new Date(`${key.slice(0, 10)}T12:00:00Z`)
  if (!DATE_KEY.test(key.slice(0, 10)) || Number.isNaN(noon.getTime())) return key
  return noon.toLocaleDateString(LOCALE, { ...options, timeZone: 'UTC' })
}

/**
 * Horário de parede no fuso → instante UTC (ISO). Num horário que não existe no fuso
 * (salto de horário de verão), devolve o instante equivalente logo após o salto.
 */
export function wallTimeToIso(local: string, zone = getTimeZone()) {
  const match = local.match(WALL_TIME)
  if (!match) throw new Error('Data e hora inválidas.')
  const [, year, month, day, hour, minute, second] = match.map(Number)
  const wall = Date.UTC(year, month - 1, day, hour, minute, second || 0)
  // Deslocamento do fuso (ms) num instante, medido no minuto cheio para ignorar os segundos.
  const offsetAt = (instant: number) => {
    const minute = Math.floor(instant / 60_000) * 60_000
    return Date.parse(`${zonedInput(minute, zone)}:00Z`) - minute
  }
  let guess = wall - offsetAt(wall)
  for (let attempt = 0; attempt < 2; attempt++) guess = wall - offsetAt(guess)
  return new Date(guess).toISOString()
}

/** Como `wallTimeToIso`, mas recusa horários inexistentes no fuso. */
export function toUtc(local: string, zone = getTimeZone()) {
  const iso = wallTimeToIso(local, zone)
  if (zonedInput(iso, zone) !== local.slice(0, 16)) {
    throw new Error('Horário inexistente no fuso selecionado.')
  }
  return iso
}

/** Início (00:00) ou fim (último milissegundo) de um dia "YYYY-MM-DD" no fuso, em UTC. */
export function dayBoundary(key: string, boundary: 'start' | 'end') {
  if (boundary === 'start') return wallTimeToIso(`${key}T00:00`)
  const nextDay = Date.parse(wallTimeToIso(`${addDaysToKey(key, 1)}T00:00`))
  return new Date(nextDay - 1).toISOString()
}

export function isWallTime(value: string) {
  return WALL_TIME.test(value)
}
